import { useEffect, useState } from 'react';
import { coreGameStateKey, parseCoreGameState } from '@/lib/gameState/admission';
import { getGameStates, hasGameStateAnalytics } from '@/services/hydraAnalytics';
import { projectPrimaryGameState } from '@/services/primaryGameState';
import type { OddsStreamGameState } from '@/services/oddsStream';

/** A retired market has no MoneylineSection to poll its score (#901).
 * Read only the configured analytics endpoint, once per route. This is final
 * display evidence, never a market resurrection or execution authority.
 */
export function useRetiredGameFinal(gameKey: string | null, enabled: boolean,
  teams: readonly [string, string]): OddsStreamGameState | null {
  const key = coreGameStateKey(gameKey);
  const active = enabled && hasGameStateAnalytics();
  const [result, setResult] = useState<{ key: string; away: string; home: string; state: OddsStreamGameState } | null>(null);
  const [away, home] = teams;
  // Retire the previous route's result before rendering another identity.
  if (result && (!active || result.key !== key || result.away !== away || result.home !== home)) {
    setResult(null);
  }
  useEffect(() => {
    if (!active || !key) return;
    const controller = new AbortController();
    let cancelled = false;
    void getGameStates([key], controller.signal).then(response => {
      if (cancelled) return;
      const state = parseCoreGameState(response.states?.[key], key);
      if (!state || state.statusId !== 3 || state.finalReason !== 'reported'
        || state.stale || state.disputed || state.awayScore === null || state.homeScore === null) return;
      const projected = projectPrimaryGameState(state, [away, home]);
      if (projected) setResult({ key, away, home, state: projected });
    }).catch(() => { /* An unavailable historical read stays unknown. */ });
    return () => { cancelled = true; controller.abort(); };
  }, [active, key, away, home]);
  return active && result?.key === key && result.away === away && result.home === home ? result.state : null;
}
